'use client'

import { useEffect, useCallback, useMemo, useRef, useState } from 'react'

import throttle from 'lodash-es/throttle'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import type { AuctionUpdates } from '@b-stock/fe-core/bstock-next/helpers'
import { subscribeToPusherUpdates } from '@b-stock/fe-core/bstock-next/helpers'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'

import type { ListingSearchContextValue } from '@/components/listings/search/ListingSearchProvider/types'
import { apply3MPAuctionUpdate } from '@/components/listings/shared/auctionUpdateUtils'
import { useEnterpriseAuctionUpdates } from '@/components/listings/shared/useEnterpriseAuctionUpdates'
import type { SearchPage, SearchResult } from '@/queries/listingSearchQuery'
import { sortListingsByEndTime } from '@/queries/listingSearchQuery'

const PUSHER_RESORT_MIN_INTERVAL_MS = 15_000

export const makeSearchDataUpdater =
  (
    listingId: string | undefined,
    listingUpdater: (listing: SearchResult) => SearchResult
  ) =>
  <T extends SearchPage>(page: T): T => {
    // Check for a match before mapping to enable the no-op same-reference fast
    // path: returning the same page reference when no listing matched prevents
    // React Query from notifying subscribers of the non-owning cache.
    const hasMatch = page.listings.some((l) => l.listingId === listingId)
    if (!hasMatch) {
      return page
    }

    return {
      ...page,
      listings: page.listings.map((listing) =>
        listing.listingId === listingId ? listingUpdater(listing) : listing
      ),
    }
  }

/**
 * Hook for real-time updates to data loaded using `ListingSearchProvider`.
 *
 * Returns changedListingIds + clearChangedListingIds for AG Grid row refresh.
 */
export function useListingSearchPusherUpdates(
  searchCtx: ListingSearchContextValue
) {
  const { data, pusherClient3MP, pusherClientEnt, updateSearchData, state } =
    searchCtx
  const { sortBy, sortOrder, isSortFrozen } = state
  const listings = useMemo(() => data?.listings ?? [], [data?.listings])
  const accountId = useDecodedAccessToken()?.accountId

  const [changedListingIds, setChangedListingIds] = useState<Set<string>>(
    new Set()
  )
  const clearChangedListingIds = useCallback(() => {
    setChangedListingIds(new Set())
  }, [setChangedListingIds])

  const resortByEndTime = useCallback(() => {
    updateSearchData((page) => ({
      ...page,
      listings: sortListingsByEndTime(page.listings, sortOrder),
    }))
  }, [sortOrder, updateSearchData])

  const throttledResort = useMemo(
    () =>
      throttle(resortByEndTime, PUSHER_RESORT_MIN_INTERVAL_MS, {
        leading: true,
        trailing: true,
      }),
    [resortByEndTime]
  )

  useEffect(() => () => throttledResort.cancel(), [throttledResort])

  // Freeze Sort (GLOB-4832): while frozen, `makeAuctionDataUpdater` below
  // skips throttledResort so row order holds even as bid/price data keeps
  // updating in place. On unfreeze, catch up immediately (bypassing the 15s
  // throttle) rather than waiting for the next Pusher push to reflect
  // whatever reordering was suppressed while frozen.
  const wasSortFrozenRef = useRef(isSortFrozen)
  useEffect(() => {
    if (wasSortFrozenRef.current && !isSortFrozen && sortBy === 'endTime') {
      resortByEndTime()
    }
    wasSortFrozenRef.current = isSortFrozen
  }, [isSortFrozen, sortBy, resortByEndTime])

  // Setup 3MP auction updates for all 3MP listings
  const auction3MPListings = useMemo(
    () => listings.filter((listing) => listing.auction3MP),
    [listings]
  )

  const makeAuctionDataUpdater = useCallback(
    (listingId: string) => (update: AuctionUpdates) => {
      let newBidStatus = BidStatus.NO_BID

      updateSearchData(
        makeSearchDataUpdater(listingId, (listing: SearchResult) => {
          const { updatedListing, bidStatus } = apply3MPAuctionUpdate(
            listing,
            update
          )
          newBidStatus = bidStatus
          return updatedListing
        })
      )

      if (sortBy === 'endTime' && !isSortFrozen) {
        throttledResort()
      }

      // Track that this listing was changed for targeted row updates
      setChangedListingIds((prev) => new Set(prev).add(listingId))

      return { bidStatus: newBidStatus }
    },
    [
      throttledResort,
      setChangedListingIds,
      sortBy,
      isSortFrozen,
      updateSearchData,
    ]
  )

  // Setup 3MP auction updates using the same logic as use3MPAuctionUpdates
  useEffect(() => {
    if (!pusherClient3MP || !accountId) {
      return
    }

    const subscriptionCleanups: Array<() => void> = []

    auction3MPListings.forEach((listing) => {
      const auction = listing.auction3MP
      if (auction) {
        const auctionId = auction._id

        // Use the same channel naming convention as use3MPAuctionUpdates
        const publicChannel = pusherClient3MP.subscribe(`auction-${auctionId}`)

        // Check for pusher channels the same way as use3MPAuctionUpdates
        const pusherChannels =
          'pusherChannels' in auction
            ? (auction as { pusherChannels?: { privateChannelName?: string } })
                .pusherChannels
            : undefined
        const privateChannel = pusherChannels?.privateChannelName
          ? pusherClient3MP.subscribe(pusherChannels.privateChannelName)
          : null

        const handleUpdate = (update: AuctionUpdates) => {
          return makeAuctionDataUpdater(listing.listingId || '')(update)
        }

        subscribeToPusherUpdates({
          publicChannel,
          privateChannel,
          auctionId,
          accountId,
          updateAuctionData: handleUpdate,
        })

        subscriptionCleanups.push(() => {
          publicChannel.unbind_all()
          privateChannel?.unbind_all()
          pusherClient3MP.unsubscribe(`auction-${auctionId}`)
          if (pusherChannels?.privateChannelName) {
            pusherClient3MP.unsubscribe(pusherChannels.privateChannelName)
          }
        })
      }
    })

    // Cleanups run when the auction3MPListings change
    return () => {
      subscriptionCleanups.forEach((cleanup) => cleanup())
    }
  }, [auction3MPListings, pusherClient3MP, accountId, makeAuctionDataUpdater])

  // Dependency of the subscription effect inside useEnterpriseAuctionUpdates, so
  // a fresh arrow each render would re-subscribe every enterprise channel on
  // every render of the provider.
  const handleEnterpriseListingUpdate = useCallback(
    (listingId: string, updatedListing: SearchResult) => {
      updateSearchData(makeSearchDataUpdater(listingId, () => updatedListing))
      setChangedListingIds((prev) => new Set(prev).add(listingId))
    },
    [updateSearchData, setChangedListingIds]
  )

  // Setup Enterprise auction updates using shared hook
  useEnterpriseAuctionUpdates({
    listings,
    pusherClientEnt,
    onListingUpdate: handleEnterpriseListingUpdate,
  })

  return {
    changedListingIds,
    clearChangedListingIds,
  }
}
